import React, { Component } from "react";

import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import { withStyles } from "@material-ui/core/styles";
import { withAuthorization } from "../Session";

import { Typography, CircularProgress } from "@material-ui/core";
import { httpsCallable } from "@firebase/functions";
import Stepper from "./Stepper";
import Actions from "./SubmitActions";
import Start from "./steps/Start";
import Business from "./steps/Business";
import Principal from "./steps/Principal";
import Account from "./steps/Account";
import Financial from "./steps/Financial";
import Processing from "./steps/Processing";
import Pricing from "./steps/Pricing";
import Addendums from "./steps/Addendums";
import { mask } from "../../util/MaskUtil";
import { reconcileLegacyMerchantEdit, applyTaxIdChoice, joinPrincipalName, CURRENCY_FIELDS, currencyInput, SOLE_PROPRIETOR, TAX_ID_PLACEHOLDER } from "../../util/ApplicationFields";
import { validateStep } from "../../util/ValidationUtil";
import Cart from "./steps/Cart";
import fields from "./steps/Pricing/fields";
import { toPaysafeEquipment } from "../../util/EquipmentUtil";
import NewApplicationStep from "./application-steps";
import { EmbeddedStep, Progress } from "./application-steps/Shell";
import { changeMerchantField, validateMerchantStep, merchantStep, PAYMENT_STEP, CONFIRM_SSN } from "./application-steps/values";
import { USE_NEW_SUBMIT_FLOW } from "./flow";
import ApplicationLocaleProvider from "../../i18n/ApplicationLocaleProvider";
import { DEFAULT_LOCALE } from "../../i18n/locale";

import { withFirebase } from "../Firebase";

import IpAddressApi from "../../api/IpAddress";

import styles from "./styles";
import {
  addDoc,
  onSnapshot,
  setDoc,
  getDoc,
  getDocs,
  doc,
  collection,
  updateDoc, 
  deleteField
} from "@firebase/firestore";

const { setHiddenPricingFields } = fields;

// Steps 0-5 hold the merchant's data and use the new merchant steps.
const FIRST_OFFICE_STEP = 6;

const shopOrderItemsFrom = storeOrder => {
  const items = storeOrder?.order?.items;
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter(item => typeof item?.name === "string" && item.name.trim())
    .map((item, index) => {
      let permalink = "";
      if (typeof item.permalink === "string") {
        try {
          const url = new URL(item.permalink);
          if (url.protocol === "http:" || url.protocol === "https:") {
            permalink = url.href;
          }
        } catch (error) {
          // An invalid product URL is rendered as plain text.
        }
      }

      const parsedQuantity = Number(item.quantity);
      const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0
        ? Math.floor(parsedQuantity)
        : 1;

      return {
        id: item.itemId || `${item.productId || "product"}-${index}`,
        name: item.name.trim(),
        quantity,
        permalink
      };
    });
};

export class SubmitApp extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeStep: 6,
      loading: false,
      savingNewApplication: false,
      promptResume: null,
      user: null,
      application: {},
      transaction: undefined,
      applicationId: props.applicationId,
      errors: {},
      isSaveButtonVisible: false,
      shopOrderItems: [],
      shopPricingPlan: null,
      shopOrderItemsLoading: false,
      equipmentInventory: [],
      equipmentInventoryLoading: true,
      paymentLoading: false,
    };
  }

  usesNewFlow = () => USE_NEW_SUBMIT_FLOW;

  handlePaymentLoading = paymentLoading => this.setState({ paymentLoading });

  loadedStoreOrderId = null;

  componentDidMount = async () => {
    const { applicationId } = this.props;
    const firestore = this.props.firebase.firestore;

    const [
      defaultEquipmentDoc,
      defaultTerminalsSnapshots,
      defaultGatewaysSnapshots
    ] = await Promise.all([
      getDoc(doc(firestore, "settings", "defaultEquipment")),
      getDocs(collection(firestore, "defaultTerminals")),
      getDocs(collection(firestore, "defaultGateways"))
    ]);
    const defaultEquipmentSettings = defaultEquipmentDoc.data();
    const defaultTerminals = defaultTerminalsSnapshots.docs.map(e => ({
      ...e.data(),
      id: e.id,
      type: 'TM',
      name: `${e.data().BrandName} ${e.data().ModelName}`
    })).sort((a, b) => a.name.localeCompare(b.name));
    const defaultGateways = defaultGatewaysSnapshots.docs.map(e => ({
      ...e.data(),
      id: e.id,
      type: 'G',
      name: `${e.data().Name}`
    })).sort((a, b) => a.name.localeCompare(b.name));

    const selectedTerminal = defaultTerminals.find(
      e => defaultEquipmentSettings?.name &&
        e.name.toLowerCase().includes(defaultEquipmentSettings.name.toLowerCase())
    ) || defaultTerminals[0];
    const selectedProcessor = selectedTerminal?.Processors?.find(
      e => defaultEquipmentSettings?.processor &&
        e.Name.toLowerCase().includes(defaultEquipmentSettings.processor.toLowerCase())
    ) || selectedTerminal?.Processors?.[0];

    this.setState({
      equipmentInventory: [...defaultTerminals, ...defaultGateways],
      equipmentInventoryLoading: false,
      defaultEquipment: selectedTerminal,
      defaultProcessor: selectedProcessor
    });

    if (applicationId) {
      onSnapshot(doc(firestore, "applications", applicationId), (snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data();
          this.setState({
            application: {
              ...data,
              isSubmitting: true,
            }
          });
          this.loadShopOrderItems(data.storeId);
        }
      });
    }
  };

  loadShopOrderItems = async storeOrderId => {
    if (!storeOrderId || this.loadedStoreOrderId === storeOrderId) {
      return;
    }

    this.loadedStoreOrderId = storeOrderId;
    this.setState({ shopOrderItemsLoading: true });

    try {
      const snapshot = await getDoc(
        doc(this.props.firebase.firestore, "storeOrders", storeOrderId)
      );
      this.setState({
        shopOrderItems: snapshot.exists()
          ? shopOrderItemsFrom(snapshot.data())
          : [],
        shopPricingPlan: snapshot.exists()
          ? snapshot.data()?.order?.pricingPlan || null
          : null,
        shopOrderItemsLoading: false
      });
    } catch (error) {
      console.error("Failed to load the shop order items.", error);
      this.setState({
        shopOrderItems: [],
        shopPricingPlan: null,
        shopOrderItemsLoading: false
      });
    }
  };

  createApplication = async (application) => {
    this.setState({ savingNewApplication: true, loading: true });

    const ipAddressResults = await IpAddressApi.getIpAddress();

    const newApplication = {
      ...application,
      activeStep: 1,
      createdAt: new Date(),
      repIpAddress: ipAddressResults.ipAddress,
    };

    const firestore = this.props.firebase.firestore;
    const d = await addDoc(
      collection(firestore, "repApplications"),
      newApplication
    );

    this.setState({
      savingNewApplication: false,
      loading: false,
      application: {
        ...newApplication,
        id: d.id,
      },
    });
    return d.id;
  };

  saveApplication = async (application) => {
    const firestore = this.props.firebase.firestore;

    return await setDoc(
      doc(firestore, "applications", application.id),
      { ...application, socialSecurityNumber: deleteField(), [CONFIRM_SSN]: deleteField() },
      { merge: true }
    );
  };

  deleteCustomPricing = async (application) => {
    const firestore = this.props.firebase.firestore;
    const docRef = doc(firestore, "applications", application.id);
    await updateDoc(docRef, {
      customPricing: deleteField(),
    });
  };

  decryptSsn = async (application) => {
    if (
      application.status !== "Sent To Api" &&
      !application.socialSecurityNumber
    ) {
      try {
        const response = await httpsCallable(
          this.props.firebase.functions,
          "decryptField"
        )({
          encryptedText: application.encryptedSocialSecurityNumber,
          salt: application.encryptedSocialSecurityNumberSalt,
        });
        application.socialSecurityNumber = response.data.decryptedText;
        // The office sees the saved SSN already confirmed; changing it asks
        // for the confirmation again.
        if (this.usesNewFlow()) application[CONFIRM_SSN] = response.data.decryptedText;
      } catch (err) {
        console.error("error decrypting sensitive data", err);
      }
    }
    else {
      console.log('skipped decrypring');
    }
  }

  handleNext = async () => {
    if (this.isValid()) {
      const steps = this.getSteps();
      if (this.state.activeStep === steps.length - 1) {
        this.handleSubmit();
      } else {
        let nextStep = this.state.activeStep + 1;
        let application = { ...this.state.application };
        // Nothing to pay: Payment is skipped, as in the merchant form.
        if (this.usesNewFlow()) nextStep = merchantStep(nextStep, application);
        if (nextStep > application.activeStep) {
          application.activeStep = nextStep;
        }
        if (this.state.activeStep === 0) {
        } else {
          if (nextStep === 2) {
            if (this.usesNewFlow() && application.ownershipType === SOLE_PROPRIETOR) {
              application.legalBusinessName = joinPrincipalName(application);
            }
            application.dateBusinessStarted =
              "01/01/" + application.businessYear;

            if (!application.mailingDifferent) {
              application.mailingAddress1 = application.businessAddress1;
              if (application.businessAddress2) {
                application.mailingAddress2 = application.businessAddress2;
              }
              application.mailingCity = application.businessCity;
              application.mailingState = application.businessState;
              application.mailingZip = application.businessZip;
            }
            if (!application.shippingDifferent) {
              application.shippingAddress1 = application.businessAddress1;
              if (application.businessAddress2) {
                application.shippingAddress2 = application.businessAddress2;
              }
              application.shippingCity = application.businessCity;
              application.shippingState = application.businessState;
              application.shippingZip = application.businessZip;
            }
            if (this.usesNewFlow() && !application.businessAddress2) {
              if (!application.mailingDifferent) application.mailingAddress2 = "";
              if (!application.shippingDifferent) application.shippingAddress2 = "";
            }
            if (!application.homePhone) {
              application.homePhone = application.businessPhone;
            }
            applyTaxIdChoice(application);

            // decrypt and show until submitted
            await this.decryptSsn(application);
          }
          if (nextStep === 3) {
            if (
              !application.encryptedSocialSecurityNumber ||
              application.socialSecurityNumber
            ) {
              const socialSecurityNumber = application.socialSecurityNumber;

              try {
                const {
                  data: { encryptedText, salt },
                } = await httpsCallable(
                  this.props.firebase.functions,
                  "encryptField"
                )({
                  plainText: socialSecurityNumber,
                });
                if (!encryptedText || !salt) throw new Error("SSN encryption failed");
                application.encryptedSocialSecurityNumber = encryptedText;
                application.encryptedSocialSecurityNumberSalt = salt;
              } catch (err) {
                this.setState({ errors: {
                  ...this.state.errors,
                  socialSecurityNumber: "We could not securely save your SSN. Please try again."
                } });
                return;
              }
              delete application.socialSecurityNumber;
              delete application[CONFIRM_SSN];
            }
            if (!application.residenceDifferent) {
              application.homeAddress1 = application.businessAddress1;
              application.homeAddress2 = application.businessAddress2 || "";
              application.homeCity = application.businessCity;
              application.homeState = application.businessState;
              application.homeZip = application.businessZip;
            }
            applyTaxIdChoice(application);
          }
          if (nextStep === 4) {
            application.monthlyAmexVolume = Math.round(
              application.cardSales * 0.1
            );
            application.cardSalesYear = Math.round(application.cardSales * 12);
          }
          if (nextStep === 6) {
            if (application.link && application.link.paymentType === "ACH") {
              application.paymentBankName = application.bankName;
              application.paymentAccountNumber = application.bankAccountNumber;
              application.paymentRoutingNumber = application.routingNumber;
            }
            if (application.paymentType === "Free") {
              nextStep += 1;
            }
          }
          if (nextStep === 7) {
            this.setEquipmentFields(application);
            application.terminalShippingEmail = application.terminalShippingEmail || application.email;
            application.runCreditReport = (application.runCreditReport === undefined ? true : application.runCreditReport);
          }
          if (nextStep === 8) {
            await this.deleteCustomPricing(application);
          }
          if (nextStep === 9) {
            setHiddenPricingFields(application);
          }
        }
        await this.saveApplication(application);
        this.setState({
          application,
          activeStep: nextStep,
          isSaveButtonVisible: nextStep === 9,
        });
      }
    }
    window.scrollTo(0, 0);
  };

  handleEquipmentChange = equipment => {
    this.setState(state => ({
      application: {
        ...state.application,
        link: {
          ...(state.application.link || {}),
          equipment
        }
      }
    }));
  };

  setEquipmentFields = application => {
    if (application.reprogram === undefined) {
      application.reprogram = true;
    }

    const hasExplicitEquipment = Array.isArray(application.link?.equipment);
    let equipmentData = hasExplicitEquipment
      ? toPaysafeEquipment(application.link.equipment)
      : [];

    if (!hasExplicitEquipment) {
      console.log('equipment - default');

      equipmentData = toPaysafeEquipment([{
        ...this.state.defaultEquipment,
        type: 'TM',
        quantity: 1,
        processor: this.state.defaultProcessor
      }]);
    }

    application.equipment = equipmentData;

    console.log('equipment', application.equipment, application);
    
  };

  handleBack = async () => {
    let application = { ...this.state.application };
    let nextStep = this.state.activeStep - 1;
    if (this.usesNewFlow() && nextStep === PAYMENT_STEP && application.withoutPayment)
      nextStep -= 1;

    if (nextStep === 2) {
      await this.decryptSsn(application);
    }

    const previousStep = nextStep;
    if (this.state.activeStep > 0) {
      this.setState({ application, activeStep: previousStep, errors: {}, paymentLoading: false, isSaveButtonVisible: false });
    }
  };

  handleReset = () => {
    this.setState({ activeStep: 0 });
  };

  canSubmit = () =>
    this.state.activeStep === this.getSteps().length - 1 &&
    this.state.isSaveButtonVisible && !this.state.loading &&
    !this.state.paymentLoading && !this.submitting &&
    Object.keys(this.state.errors).length === 0 &&
    (!this.state.sent || this.state.application.status === "Api Errors");

  handleSaveAndSend = async () => {
    if (!this.canSubmit()) return;
    this.submitting = true;
    this.setState({ loading: true });
    try {
      const { application } = this.state;
      application.status = "Sending To Api";
      application.error = "";
      application.validation = "";
      await this.saveApplication(application);

      this.setState({ sent: true });
      this.props.onSave && this.props.onSave(application.id);
    } catch (error) {
      console.error("Error saving and sending application:", error);
    } finally {
      this.submitting = false;
      this.setState({ loading: false });
    }
  };

  handleSubmit = async () => {
    this.setState({ loading: true });
  };

  handleResetSsn = () => {
    const application = { ...this.state.application };
    application.encryptedSocialSecurityNumber = "";
    application.socialSecurityNumber = "";
    application[CONFIRM_SSN] = "";
    this.setState({ application });
  };

  handleApplicationTextChange = (name, container) => (event) => {
    if (!event) {
      return;
    }

    let val = event.target.value;

    if (this.usesNewFlow() && !container && this.state.activeStep < FIRST_OFFICE_STEP) {
      this.setState(state => ({
        application: changeMerchantField(state.application, name, val)
      }));
      return;
    }

    const application = { ...this.state.application };
    if (container) {
      application[container][name] = mask(name, val);
    } else {
      application[name] = mask(name, val);
      if (CURRENCY_FIELDS.includes(name)) {
        const raw = currencyInput(val);
        if (raw === null) return;
        application[name] = raw;
      }
    }

    if (
      name === "ownershipType" &&
      val !== SOLE_PROPRIETOR
    ) {
      application.soleProprietorHasEin = "";
      if (application.federalTaxId === TAX_ID_PLACEHOLDER) application.federalTaxId = "";
    }

    if (!container) reconcileLegacyMerchantEdit(application, name);

    if (name === "office") {
      application.salesRepInfo = null;
    }

    if (name === "customPricingType") {
      application.customPricing = {};
    }

    this.setState({ application });
  };

  handleApplicationAutocompleteChange = (name, container) => (event, value) => {
    if (!event) {
      return;
    }
    let val = (value === undefined) ? event.target.value : value;
    if (!val && name === "soldProductTypes") {
      val = event.target.textContent;
    }

    const application = { ...this.state.application };
    if (container) {
      application[container][name] = mask(name, val);
    } else {
      application[name] = mask(name, val);
    }
    this.setState({ application });
  };

  handleCheckChange = (name) => (event) => {
    let application = { ...this.state.application };
    application[name] = event.target.checked;
    if (name === "mailingDifferent" && event.target.checked) {
      application.mailingAddress1 = "";
      application.mailingAddress2 = "";
      application.mailingCity = "";
      application.mailingState = "";
      application.mailingZip = "";
    } 
    if (name === "shippingDifferent" && event.target.checked) {
      application.shippingAddress1 = "";
      application.shippingAddress2 = "";
      application.shippingCity = "";
      application.shippingState = "";
      application.shippingZip = "";
    }
    if (name === "residenceDifferent" && event.target.checked) {
      application.homeAddress1 = "";
      application.homeAddress2 = "";
      application.homeCity = "";
      application.homeState = "";
      application.homeZip = "";
    }
    this.setState({ application });
  };

  handlePlanChange = (event) => {
    let application = { ...this.state.application };
    application.plan = event.target.value;
    this.setState({ application });
  };

  setApplicationPricing = (pricing) => {
    let application = { ...this.state.application };
    application.pricing = pricing;
    this.setState({ application });
  };

  getSteps = () => {
    return [
      "Email Address",
      "Business Info",
      "Principal Info",
      "Account Info",
      "Financial Info",
      "Payment Info",
      "Processing Info",
      "Credit card sales and Pricing",
      "Addendums",
      "Done",
    ];
  };

  showError = (application) => {
    if (application.error && application.error.Results) {
      return application.error.Results.map((item) => (
        <p key={item.ReferenceID}>
          {item.Message} ({item.ReferenceID})
        </p>
      ));
    }
    if (application.error && application.error.Message) {
      return <p>{application.error.Message}</p>
    }
  };

  showValidationError = (application) => {
    if (application.validation && application.validation[0]) {
      return application.validation[0].Results.map((item) => (
        <div key={item.ReferenceID}>
          {item.Message} {item.ReferenceID}
        </div>
      ));
    }
  };

  getStepContent = (stepIndex, application, errors) => {
    if (this.usesNewFlow() && stepIndex < FIRST_OFFICE_STEP) {
      return <NewApplicationStep key={stepIndex} step={stepIndex} application={application}
        errors={errors} onApplicationTextChange={this.handleApplicationTextChange}
        onCheckChange={this.handleCheckChange} resetSsn={this.handleResetSsn}
        setApplicationPricing={this.setApplicationPricing}
        onPaymentLoading={this.handlePaymentLoading} />;
    }
    switch (stepIndex) {
      case 0:
        return (
          <Start
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 1:
        return (
          <Business
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            onCheckChange={this.handleCheckChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <Principal
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            onCheckChange={this.handleCheckChange}
            errors={errors}
            isSsnEncrypted={
              application.status === "Sent To Api" &&
              application.encryptedSocialSecurityNumber
            }
            resetSsn={this.handleResetSsn}
          />
        );
      case 3:
        return (
          <Account
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <Financial
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 5:
        return (
          <Cart
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            setApplicationPricing={this.setApplicationPricing}
            onCheckChange={this.handleCheckChange}
            onBack={this.handleBack}
            onNext={this.handleNext}
            errors={errors}
            transaction={this.state.transaction}
          />
        );
      case 6:
        return (
          <Processing
            application={application}
            shopOrderItems={this.state.shopOrderItems}
            shopOrderItemsLoading={this.state.shopOrderItemsLoading}
            equipmentInventory={this.state.equipmentInventory}
            equipmentInventoryLoading={this.state.equipmentInventoryLoading}
            defaultEquipment={this.state.defaultEquipment}
            defaultProcessor={this.state.defaultProcessor}
            onEquipmentChange={this.handleEquipmentChange}
            onApplicationTextChange={this.handleApplicationTextChange}
            setApplicationPricing={this.setApplicationPricing}
            onCheckChange={this.handleCheckChange}
            onBack={this.handleBack}
            onNext={this.handleNext}
            errors={errors}
            transaction={this.state.transaction}
          />
        );
      case 7:
        return (
          <Pricing
            application={application}
            storePricingPlan={this.state.shopPricingPlan}
            onApplicationTextChange={this.handleApplicationTextChange}
            onApplicationAutocompleteChange={this.handleApplicationAutocompleteChange}
            setApplicationPricing={this.setApplicationPricing}
            onCheckChange={this.handleCheckChange}
            onBack={this.handleBack}
            onNext={this.handleNext}
            errors={errors}
            transaction={this.state.transaction}
          />
        );
      case 8:
        return (
          <Addendums
            applicationId={this.props.applicationId}
            onApplicationTextChange={this.handleApplicationTextChange}
            setApplicationPricing={this.setApplicationPricing}
            onCheckChange={this.handleCheckChange}
            onBack={this.handleBack}
            onNext={this.handleNext}
            errors={errors}
            transaction={this.state.transaction}
          />
        );
      case 9:
        return (
          <div className={this.props.classes.done}>
            {this.state.sent ? (
              <Typography>
                <p>Application will be submitted to Paysafe</p>
                <div>
                  {(application.status === "Sending To Api" ||
                    application.status === "Uploading Documents") && (
                    <p>
                      <CircularProgress size={24} color="inherit" />
                    </p>
                  )}
                  {application.status}
                </div>
                {this.showError(application)}
                {this.showValidationError(application)}
              </Typography>
            ) : (
              <Typography>
                You will see the submittion result on the Applications page.
              </Typography>
            )}
          </div>
        );
      default:
        throw RangeError("Invalid step");
    }
  };

  isValid = () => {
    const { activeStep, application } = this.state;
    const validationResult =
      this.usesNewFlow() && activeStep < FIRST_OFFICE_STEP
        ? validateMerchantStep(activeStep, application)
        : validateStep(activeStep, application);
    if (!validationResult.isValid) {
      this.setState({ errors: validationResult.errors });
    } else {
      this.setState({ errors: {} });
    }
    return validationResult.isValid;
  };

  render() {
    const { classes } = this.props;
    const {
      activeStep,
      application,
      loading,
      errors,
    } = this.state;
    const steps = this.getSteps();
    const stepIndex = activeStep;

    return (
      <div className={classes.root}>
        {this.usesNewFlow() || <Stepper steps={steps} activeStep={stepIndex} />}
        {this.usesNewFlow() ? (
          <ApplicationLocaleProvider locale={DEFAULT_LOCALE}>
            <EmbeddedStep activeStep={activeStep} wide={activeStep === 1}
              legacy={activeStep >= FIRST_OFFICE_STEP}
              progress={
                <Progress activeStep={activeStep} application={application} labels={steps} />
              }>
              {this.getStepContent(activeStep, application, errors)}
            </EmbeddedStep>
          </ApplicationLocaleProvider>
        ) : (
          this.getStepContent(activeStep, application, errors)
        )}
        <Actions
          application={application}
          steps={steps}
          activeStep={activeStep}
          onNext={this.handleNext}
          onSave={this.handleSaveAndSend}
          onBack={this.handleBack}
          loading={loading || this.state.paymentLoading}
          showSubmit={this.canSubmit()}
          showBack={!this.state.sent || application.status === "Api Errors"}
          showNext={!this.state.sent || application.status === "Api Errors"}
        />
      </div>
    );
  }
}

export default compose(
  withRouter,
  withStyles(styles),
  withAuthorization(user => !!user),
  withFirebase
)(SubmitApp);
